import { configureStore } from "@reduxjs/toolkit";
import cartReducer, { CART_STORAGE_KEY, parseCart, replaceCart, type CartState } from "./slices/cartSlice";

export const store = configureStore({
  reducer: {
    cart: cartReducer,
  },
});

interface CartStore {
  getState: () => { cart: CartState };
  dispatch: (action: ReturnType<typeof replaceCart>) => unknown;
  subscribe: (listener: () => void) => () => void;
}

/**
 * Persists the cart so a refresh doesn't wipe what the user picked, and
 * keeps tabs in step: a cart changed (or cleared by checkout) in one tab is
 * picked up by the others, so a stale tab can't write it back. Only the cart
 * slice is persisted, and only when it changes; the sign-in session keeps
 * its own localStorage keys (common/auth/session.ts). Returns a function
 * that undoes both.
 */
export function persistCart(target: CartStore, win: Window = window): () => void {
  let last = target.getState().cart;

  const unsubscribe = target.subscribe(() => {
    const cart = target.getState().cart;
    if (cart === last) return;
    last = cart;
    try {
      const value = JSON.stringify(cart);
      // Skips the write-back of a cart that just arrived from another tab.
      if (win.localStorage.getItem(CART_STORAGE_KEY) !== value) {
        win.localStorage.setItem(CART_STORAGE_KEY, value);
      }
    } catch {
      /* ignore storage errors (private mode, quota) */
    }
  });

  const onStorage = (e: StorageEvent) => {
    // key is null when another tab called localStorage.clear().
    if (e.key !== CART_STORAGE_KEY && e.key !== null) return;
    const next = parseCart(e.key === null ? null : e.newValue);
    if (JSON.stringify(next) === JSON.stringify(target.getState().cart)) return;
    target.dispatch(replaceCart(next));
  };
  win.addEventListener("storage", onStorage);

  return () => {
    unsubscribe();
    win.removeEventListener("storage", onStorage);
  };
}

if (typeof window !== "undefined") {
  persistCart(store);
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
